import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories, categoryVideos, hotVideos } from '@/mocks/videos';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

type VideoItem = {
  id: number;
  title: string;
  category: string;
  views: number;
  rating: number;
  duration: string;
  thumbnail: string;
  year?: number;
  region?: string;
  episodes?: number;
  actors?: string[];
  director?: string;
  host?: string;
  studio?: string;
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSort, setActiveSort] = useState('hot');

  const currentCategory = categories.find((c) => c.slug === slug);
  const videos = categoryVideos[slug as keyof typeof categoryVideos] || [];
  const allVideos = [...videos, ...hotVideos.filter((v) => v.category === slug).map((v) => ({
    id: v.id,
    title: v.title,
    category: v.category,
    views: v.views,
    rating: v.rating,
    duration: v.duration,
    thumbnail: v.thumbnail,
    year: 2024,
    region: '中国大陆',
  }))].filter((v, i, arr) => arr.findIndex((x) => x.id === v.id) === i);

  const sortedVideos = [...allVideos].sort((a, b) => {
    if (activeSort === 'hot') return b.views - a.views;
    if (activeSort === 'rating') return b.rating - a.rating;
    return 0;
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [slug]);

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-400"></i>
            <p className="mt-4 text-foreground-600">分类未找到</p>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 text-sm cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
            <Breadcrumb />
            <div className="flex items-center gap-4 mt-2">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-50">
                <i className={`${currentCategory.icon} text-2xl text-primary-500`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  共 {sortedVideos.length} 部{currentCategory.name}资源
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
          <div className="flex items-center gap-3 mb-6">
            <span className="text-sm text-foreground-500">排序：</span>
            {[
              { key: 'hot', label: '最热' },
              { key: 'rating', label: '高分' },
            ].map((s) => (
              <button
                key={s.key}
                onClick={() => setActiveSort(s.key)}
                className={`px-4 py-1.5 text-sm rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                  activeSort === s.key
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {sortedVideos.map((video: VideoItem) => (
              <Link
                key={video.id}
                to={`/article/${video.id}`}
                className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
              >
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-md">
                    {video.duration}
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/90">
                      <i className="ri-play-fill text-white text-xl ml-0.5"></i>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                    {video.title}
                  </h3>
                  <div className="flex items-center gap-3 mt-2 text-xs text-foreground-500">
                    {video.year && <span>{video.year}</span>}
                    {video.region && <span>{video.region}</span>}
                    {video.episodes && <span>{video.episodes}集</span>}
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-xs">
                    <span className="text-primary-500 flex items-center gap-1">
                      <i className="ri-star-fill text-amber-400"></i>
                      {video.rating}
                    </span>
                    <span className="text-foreground-500">
                      {video.views >= 10000 ? `${(video.views / 10000).toFixed(0)}万次播放` : `${video.views}次播放`}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {sortedVideos.length === 0 && (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-5xl text-foreground-300"></i>
              <p className="mt-4 text-foreground-500">暂无该分类的视频资源</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}